<template>
  <section class="robots-panel">
    <article class="mobile-robot-summary">
      <div class="mobile-robot-summary__grid">
        <div>
          <span>运行中</span>
          <strong>{{ runningCount }} <small>台</small></strong>
        </div>
        <div>
          <span>今日收益 (USDT)</span>
          <strong :class="valueTone(earnings.todayProfit)">{{ signed(earnings.todayProfit) }}</strong>
        </div>
        <div>
          <span>今日执行</span>
          <strong>{{ todayExecutionText }} <small>单</small></strong>
        </div>
        <div>
          <span>平均健康度</span>
          <strong>{{ averageHealthText }}</strong>
        </div>
      </div>
      <button
        class="mobile-robot-summary__power"
        :class="robotPowerState.tone"
        type="button"
        :disabled="powerLoading || robotPowerState.disabled"
        @click="$emit('robot-power')"
      >
        <span
          v-if="!powerLoading && robotPowerState.mode === 'running'"
          class="mobile-robot-summary__bars"
          aria-hidden="true"
        ><i /><i /><i /><i /></span>
        <i v-else :class="powerLoading ? 'el-icon-loading' : robotPowerState.icon" />
        {{ powerLoading ? '正在处理' : (robotPowerState.mode === 'running' ? [$t('机器人运行中'), $t('点击关闭机器人')].join(' · ') : robotPowerState.text) }}
      </button>
    </article>

    <div class="robots-page-head">
      <div>
        <h1>机器人</h1>
        <p>创建、监控与管理全部自动交易策略</p>
      </div>
      <div class="robots-page-head__actions">
        <button
          class="robot-power"
          :class="robotPowerState.tone"
          type="button"
          :disabled="powerLoading || robotPowerState.disabled"
          :title="robotPowerState.title"
          @click="$emit('robot-power')"
        >
          <i v-if="powerLoading" class="el-icon-loading" />
          <svg
            v-else-if="robotPowerState.mode === 'running'"
            class="robot-power__bars"
            width="15"
            height="14"
            viewBox="0 0 15 14"
            fill="none"
            aria-hidden="true"
          >
            <rect x=".5" y="6" width="2.6" height="8" rx="1.3" fill="currentColor">
              <animate attributeName="height" values="5;10;5" dur="1.1s" repeatCount="indefinite" />
              <animate attributeName="y" values="9;4;9" dur="1.1s" repeatCount="indefinite" />
            </rect>
            <rect x="4.5" y="3" width="2.6" height="11" rx="1.3" fill="currentColor">
              <animate attributeName="height" values="11;5;11" dur="1.3s" repeatCount="indefinite" />
              <animate attributeName="y" values="3;9;3" dur="1.3s" repeatCount="indefinite" />
            </rect>
            <rect x="8.5" y="7" width="2.6" height="7" rx="1.3" fill="currentColor">
              <animate attributeName="height" values="7;12;7" dur=".9s" repeatCount="indefinite" />
              <animate attributeName="y" values="7;2;7" dur=".9s" repeatCount="indefinite" />
            </rect>
            <rect x="12.5" y="5" width="2.6" height="9" rx="1.3" fill="currentColor">
              <animate attributeName="height" values="9;4;9" dur="1.2s" repeatCount="indefinite" />
              <animate attributeName="y" values="5;10;5" dur="1.2s" repeatCount="indefinite" />
            </rect>
          </svg>
          <svg
            v-else-if="robotPowerState.mode === 'start'"
            class="robot-power__play"
            width="13"
            height="13"
            viewBox="0 0 14 14"
            fill="none"
            aria-hidden="true"
          >
            <path d="M4 2.5 11.5 7 4 11.5Z" fill="currentColor" />
          </svg>
          <i v-else :class="robotPowerState.icon" />
          {{ powerLoading ? '正在处理' : robotPowerState.text }}
        </button>
        <button class="robot-export" type="button" @click="exportHistory">
          导出历史记录
        </button>
      </div>
    </div>

    <article class="robot-status-card">
      <div class="robot-stat">
        <span>运行中机器人</span>
        <strong>{{ runningCount }} <small>台</small></strong>
      </div>
      <i class="robot-stat-divider" />
      <div class="robot-stat">
        <span>今日执行</span>
        <strong>{{ todayExecutionText }} <small>单</small></strong>
      </div>
      <i class="robot-stat-divider" />
      <div class="robot-stat">
        <span>今日收益 (USDT)</span>
        <strong :class="valueTone(earnings.todayProfit)">{{ signed(earnings.todayProfit) }}</strong>
      </div>
      <i class="robot-stat-divider" />
      <div class="robot-stat">
        <span>本月收益 (USDT)</span>
        <strong :class="valueTone(earnings.monthProfit)">{{ signed(earnings.monthProfit) }}</strong>
      </div>
      <i class="robot-stat-divider" />
      <div class="robot-stat">
        <span>平均保证金健康度</span>
        <strong>{{ averageHealthText }}</strong>
      </div>
      <div class="robot-system-pill" :class="systemStatus.tone">
        <i />
        <span>{{ systemStatus.text }} · {{ exchange }}</span>
      </div>
    </article>

    <div class="robot-section-title product-title">
      <div>
        <i class="blue" />
        <h2>产品与策略</h2>
        <span class="section-tag blue">策略市场</span>
      </div>
      <div class="product-title__meta">
        <span>共 {{ visibleProducts.length }} 个策略产品</span>
        <span class="mobile-product-swipe">{{ $t('共') }}{{ visibleProducts.length }}{{ $t('个策略') }}·{{ $t('点击展开') }}</span>
        <button type="button" @click="productsExpanded = !productsExpanded">
          {{ productsExpanded ? '收起' : '展开全部' }}
          <i :class="productsExpanded ? 'el-icon-arrow-up' : 'el-icon-arrow-down'" />
        </button>
      </div>
    </div>

    <div class="featured-products">
      <button
        v-for="item in featuredProducts"
        :key="item.product.key"
        class="featured-product"
        type="button"
        @click="$emit('create', item.product.key)"
      >
        <i class="featured-product__sweep" />
        <span class="featured-product__icon" :class="item.iconTone">
          <img :src="item.icon" alt="" />
        </span>
        <span class="featured-product__body">
          <span class="featured-product__name">
            <strong>{{ item.product.name }}</strong>
            <em>{{ item.label }}</em>
          </span>
          <small>{{ item.description }} · 在运行 <b>{{ productRobotCount(item.product) }}</b> 台</small>
        </span>
        <span class="featured-product__performance">
          <small>近 30 日</small>
          <strong>--</strong>
        </span>
        <span class="featured-product__create"><i class="el-icon-plus" /> 立即创建</span>
      </button>
    </div>

    <div class="mobile-product-market" aria-label="产品与策略">
      <article
        v-for="(group, groupIndex) in productGroups"
        :key="'mobile-group-' + group.name"
        class="mobile-product-group"
        :class="{ 'is-open': mobileProductGroupOpen(group, groupIndex) }"
      >
        <button
          class="mobile-product-group__head"
          type="button"
          :aria-expanded="mobileProductGroupOpen(group, groupIndex) ? 'true' : 'false'"
          @click="toggleMobileProductGroup(group, groupIndex)"
        >
          <span class="mobile-product-group__icon" :style="{ background: group.products[0].gradient }">
            <img :src="iconFor(group.products[0].icon)" alt="" />
          </span>
          <span class="mobile-product-group__copy">
            <strong>{{ group.name }}</strong>
            <small>{{ mobileProductGroupSubtitle(group) }}</small>
          </span>
          <em>{{ group.products.length }}个策略</em>
          <i class="el-icon-arrow-down" aria-hidden="true" />
        </button>

        <button
          v-if="canPreviewGroup(group)"
          v-show="mobileProductGroupOpen(group, groupIndex)"
          class="mobile-product-group__preview"
          type="button"
          @click.stop="openPositionPreview(group)"
        >
          <i class="el-icon-view" />
          {{ $tk('robot.Strategy_position_preview') }}
          <i class="el-icon-arrow-right" />
        </button>

        <div v-show="mobileProductGroupOpen(group, groupIndex)" class="mobile-product-group__body">
          <button
            v-for="product in group.products"
            :key="'mobile-' + product.key"
            class="mobile-product-row"
            type="button"
            @click="$emit('create', product.key)"
          >
            <span class="mobile-product-row__copy">
              <span class="mobile-product-row__name">
                <strong>{{ product.name }}</strong>
                <em :style="mobileProductTagStyle(product)">{{ mobileProductTag(product) }}</em>
              </span>
              <small>{{ mobileProductDescription(product) }}</small>
              <span class="mobile-product-row__stats">
                <span>近 30 日 <b :class="valueTone(mobileProductView(product).returnValue)">{{ mobileProductView(product).returnText }}</b></span>
                <span>风险 <b :style="mobileProductRiskStyle(product)">{{ mobileProductRisk(product) }}</b></span>
              </span>
            </span>
            <span class="mobile-product-row__aside">
              <span class="mobile-product-row__plot">
                <EChartLine
                  v-if="mobileProductView(product).trendPoints.length"
                  :points="mobileProductView(product).trendPoints"
                  :labels="mobileProductView(product).trendLabels"
                  :stroke-width="2"
                  :area-opacity="0.12"
                  :smooth="0.32"
                  :show-axis="false"
                  :grid-top="2"
                  :grid-bottom="1"
                  :aria-label="product.name + ' 近 30 日收益趋势'"
                />
              </span>
              <span class="mobile-product-row__create"><i class="el-icon-plus" /> 创建</span>
            </span>
          </button>
        </div>
      </article>
    </div>
    <div v-if="productsExpanded" class="product-groups">
      <article
        v-for="group in productGroups"
        :key="group.name"
        class="product-group"
      >
        <header>
          <div>
            <i :style="{ background: group.products[0].gradient }" />
            <strong>{{ group.name }}</strong>
            <em>{{ group.products.length }}个策略</em>
          </div>
          <button
            v-if="canPreviewGroup(group)"
            class="product-group__preview"
            type="button"
            @click="openPositionPreview(group)"
          >
            <i class="el-icon-view" />
            {{ $tk('robot.Strategy_position_preview') }}
          </button>
        </header>
        <button
          v-for="product in group.products"
          :key="product.key"
          class="product-row"
          type="button"
          @click="$emit('create', product.key)"
        >
          <span class="product-row__icon" :style="{ background: product.gradient }">
            <img :src="iconFor(product.icon)" alt="" />
          </span>
          <span class="product-row__copy">
            <span class="product-row__name">
              <strong>{{ product.name }}</strong>
              <em v-if="product.hot" class="hot">
                <i class="el-icon-s-flag" /> 推荐 · 热门
              </em>
              <em
                v-for="tag in product.tags"
                :key="tag.text"
                :style="{ color: tag.color, background: tag.background }"
              >
                {{ tag.text }}
              </em>
            </span>
            <small>{{ mobileProductDescription(product) }}</small>
          </span>
          <span class="product-row__add"><i class="el-icon-plus" /></span>
        </button>
      </article>
    </div>

    <div v-else class="product-chips">
      <button
        v-for="product in visibleProducts"
        :key="product.key"
        type="button"
        @click="$emit('create', product.key)"
      >
        <i :style="{ background: product.gradient }" />
        {{ product.name }}
        <strong><i class="el-icon-plus" /></strong>
      </button>
      <button class="product-chips__more" type="button" @click="productsExpanded = true">
        展开查看详情 <i class="el-icon-arrow-right" />
      </button>
    </div>

    <div class="robot-section-title current-title">
      <div class="current-title__main">
        <i class="green" />
        <button
          class="strategy-list-tab"
          type="button"
          role="tab"
          :class="{ active: strategyMode === 'current' }"
          :aria-selected="strategyMode === 'current' ? 'true' : 'false'"
          @click="$emit('strategy-mode-change', 'current')"
        >
          {{ $tk('robot.current_strategy') }}
        </button>
          <span class="section-tag green">{{ $t('我的机器人') }}</span>
          <span class="current-title__summary">
            <i />
            {{ runningCount }} {{ $t('台运行中') }} · {{ $t('今日') }}
            <strong :class="valueTone(earnings.todayProfit)">{{ signed(earnings.todayProfit) }}</strong>
            USDT
          </span>
      </div>
      <button
        class="strategy-list-tab strategy-list-tab--history"
        type="button"
        role="tab"
        :class="{ active: strategyMode === 'history' }"
        :aria-selected="strategyMode === 'history' ? 'true' : 'false'"
        @click="$emit('strategy-mode-change', 'history')"
      >
        {{ $tk('robot.historical_strategy') }}
      </button>
    </div>
    <article class="robot-list-card">
      <div class="robot-list-toolbar">
        <span>{{ $t('共') }} {{ displayStrategyTotal }} {{ $t('条策略') }}</span>
        <div>
          <div class="robot-sort">
            <button :class="{ active: sort === 'default' }" type="button" @click="sort = 'default'">
              默认
            </button>
            <button :class="{ active: sort === 'profit' }" type="button" @click="sort = 'profit'">
              收益 <i class="el-icon-bottom" />
            </button>
            <button :class="{ active: sort === 'health' }" type="button" @click="sort = 'health'">
              健康度 <i class="el-icon-bottom" />
            </button>
          </div>
          <span class="robot-list-current-exchange">
            <i />
            {{ exchange }}
          </span>
        </div>
      </div>

      <div v-if="!visibleRobots.length" class="robot-empty">
        <span><img :src="emptyRobotIcon" alt="" /></span>
        <strong>
          {{ strategyListLoading
            ? $tk('robot.strategy_list_loading')
            : (isHistoryMode ? $tk('robot.history_strategy_empty') : $t('还没有运行中的策略')) }}
        </strong>
        <p v-if="!isHistoryMode">
          从上方「产品与策略」选择一个模板，三步即可启动你的第一台机器人；<br />
          风险保护与保证金监控将自动开启。
        </p>
        <button v-if="!isHistoryMode" type="button" @click="$emit('create', 'astra')">
          <i class="el-icon-plus" /> 启动新机器人
        </button>
      </div>

      <article
        v-for="robot in visibleRobots"
        v-else
        :key="robot.id"
        class="robot-strategy"
      >
        <section
          class="mobile-strategy-card"
          role="button"
          tabindex="0"
          @click="$emit('robot', robot)"
          @keydown.enter="$emit('robot', robot)"
          @keydown.space.prevent="$emit('robot', robot)"
        >
          <header class="mobile-strategy-card__head">
            <strong>{{ mobileStrategyTitle(robot) }}</strong>
            <button
              v-if="!isHistoryMode && maxPositionException(robot)"
              class="mobile-strategy-card__status attention"
              type="button"
              @click.stop="$emit('robot-exception', robot)"
            >
              <i />已暂停
            </button>
            <em v-else-if="!isHistoryMode" class="mobile-strategy-card__status" :class="effectiveStatus(robot)">
              <i />{{ statusText(effectiveStatus(robot)) }}
            </em>
          </header>
          <p>{{ mobileStrategyMeta(robot) }}</p>
          <div class="mobile-strategy-card__metrics">
            <div>
              <span>今日收益 ({{ robot.profitUnit || 'USDT' }})</span>
              <strong :class="valueTone(mobileTodayProfitValue(robot))">
                {{ mobileTodayProfitText(robot) }}
              </strong>
            </div>
            <div>
              <span>30 日收益率</span>
              <strong :class="valueTone(robot.rateValue)">{{ robot.rate }}</strong>
            </div>
          </div>
          <div class="mobile-strategy-card__health">
            <i><b :style="{ width: healthWidth(robot), background: healthColor(robot) }" /></i>
            <span>健康度 {{ healthText(robot) }}</span>
          </div>
          <div class="mobile-strategy-card__actions">
            <button
              v-if="isHistoryMode"
              type="button"
              class="primary"
              @click.stop="$emit('robot', robot)"
            >
              {{ $tk('robot.Strategy_details') }}
            </button>
            <button
              v-if="!isHistoryMode && canModify(robot)"
              type="button"
              class="primary"
              @click.stop="$emit('robot-menu', { action: 'modify', robot })"
            >
              {{ $t('修改') }}
            </button>
            <button
              v-if="!isHistoryMode && actionCapabilities(robot).pauseResume && robot.status === 'running'"
              type="button"
              @click.stop="$emit('robot-menu', { action: 'pause', robot })"
            >
              {{ $t('暂停') }}
            </button>
            <button
              v-else-if="!isHistoryMode && actionCapabilities(robot).pauseResume"
              type="button"
              class="resume"
              @click.stop="$emit('robot-menu', { action: 'resume', robot })"
            >
              {{ $t('恢复') }}
            </button>
            <button
              v-if="!isHistoryMode && actionCapabilities(robot).delete"
              type="button"
              class="danger"
              @click.stop="$emit('robot-menu', { action: 'delete', robot })"
            >
              {{ $t('删除') }}
            </button>
          </div>
        </section>

        <div class="robot-strategy__head">
          <span class="robot-strategy__icon" :style="{ background: productGradient(robot) }">
            <img :src="productIcon(robot)" alt="" />
          </span>
          <button class="robot-strategy__identity" type="button" @click="$emit('robot', robot)">
            <strong>
              ({{ robot.rankText }})
              <template v-if="robotPairParts(robot).length === 2 && robot.productType !== 'astra'">
                {{ robotPairParts(robot)[0] }}
                <span
                  class="robot-pair-arrow"
                  :class="`robot-pair-arrow--${robot.positionDirection || 'right'}`"
                  aria-hidden="true"
                >
                  {{ positionArrow(robot) }}
                </span>
                {{ robotPairParts(robot)[1] }}
              </template>
              <template v-else>{{ robotPair(robot) }}</template>
            </strong>
            <small>[{{ robotKind(robot) }}] {{ shortCreatedAtText(robot) }}</small>
          </button>
          <span class="robot-strategy__tag">{{ robot.automation }} · {{ robot.name }}</span>
          <span v-if="robot.productType === 'hedge'" class="robot-strategy__tag muted">
            止损 {{ stopLossText(robot) }}
          </span>
          <!-- 策略列表走势图暂时隐藏，后续需要时恢复。 -->
          <!-- <span class="robot-strategy__trend">暂无走势</span> -->
          <span v-if="!isHistoryMode" class="robot-strategy__states">
            <em :class="riskStatus(robot).tone">{{ riskStatus(robot).text }}风险</em>
            <button
              v-if="!isHistoryMode && maxPositionException(robot)"
              class="robot-strategy__exception-state"
              type="button"
              title="达到最大持仓，点击查看处理方式"
              @click="$emit('robot-exception', robot)"
            >
              <i class="el-icon-warning-outline" />
              已暂停
            </button>
            <em v-else :class="effectiveStatus(robot)">
              <i />{{ statusText(effectiveStatus(robot)) }}
            </em>
          </span>
        </div>
        <div class="robot-strategy__content">
          <div class="robot-metrics">
            <div>
              <span>投入金额 ({{ robot.investmentUnit || 'USDT' }})</span>
              <strong>{{ robot.investment }}</strong>
            </div>
            <div>
              <span>总收益 ({{ robot.profitUnit || 'USDT' }})</span>
              <strong :class="valueTone(robot.profitValue)">
                <i v-if="profitIcon(robot.profitValue)" :class="profitIcon(robot.profitValue)" />
                {{ robot.profit }}
              </strong>
            </div>
            <div>
              <span>收益率</span>
              <strong :class="valueTone(robot.rateValue)">{{ robot.rate }}</strong>
            </div>
            <div>
              <span>保证金健康度</span>
              <span class="robot-health">
                <i><b :style="{ width: healthWidth(robot), background: healthColor(robot) }" /></i>
                <strong>{{ healthText(robot) }}</strong>
              </span>
            </div>
            <div>
              <span>{{ isHistoryMode ? $tk('robot.delete_time') : $tk('robot.creation_time') }}</span>
              <strong class="created-at">{{ strategyTimeText(robot) }}</strong>
            </div>
          </div>
          <div class="robot-strategy__actions">
            <button
              v-if="isHistoryMode"
              type="button"
              class="primary"
              @click.stop="$emit('robot', robot)"
            >
              {{ $tk('robot.Strategy_details') }}
            </button>
            <button
              v-if="!isHistoryMode && canModify(robot)"
              type="button"
              class="primary"
              @click="$emit('robot-menu', { action: 'modify', robot })"
            >
              修改
            </button>
            <button
              v-if="!isHistoryMode && actionCapabilities(robot).pauseResume && robot.status === 'running'"
              type="button"
              @click="$emit('robot-menu', { action: 'pause', robot })"
            >
              暂停
            </button>
            <button
              v-else-if="!isHistoryMode && actionCapabilities(robot).pauseResume"
              type="button"
              class="resume"
              @click="$emit('robot-menu', { action: 'resume', robot })"
            >
              恢复
            </button>
            <button
              v-if="!isHistoryMode && actionCapabilities(robot).delete"
              type="button"
              class="danger"
              @click="$emit('robot-menu', { action: 'delete', robot })"
            >
              删除
            </button>
          </div>
        </div>
      </article>
      <div v-if="isHistoryMode && historyPageCount > 1" class="history-strategy-pagination">
        <button
          type="button"
          :disabled="historyPage <= 1 || strategyListLoading"
          @click="$emit('history-page-change', historyPage - 1)"
        >
          <i class="el-icon-arrow-left" /> {{ $t('上一页') }}
        </button>
        <span>{{ historyPage }} / {{ historyPageCount }}</span>
        <button
          type="button"
          :disabled="historyPage >= historyPageCount || strategyListLoading"
          @click="$emit('history-page-change', historyPage + 1)"
        >
          {{ $t('下一页') }} <i class="el-icon-arrow-right" />
        </button>
      </div>
    </article>

    <StrategyPositionPreviewModal
      :visible.sync="positionPreviewVisible"
      :category="positionPreviewCategory"
      :robot-type="robotType"
      :exchange="exchange"
    />
  </section>
</template>

<script>
import {
  finiteNumber,
  firstNumber,
  formatNumber,
  formatSigned
} from '@/utils/portal-data'
import { STRATEGY_PRODUCTS } from '@/api/strategy'
import EChartLine from '@/components/rebuild/EChartLine.vue'
import StrategyPositionPreviewModal from '@/components/rebuild/StrategyPositionPreviewModal.vue'
import {
  robotActionCapabilities,
  robotNeedsMaximumRestart,
  robotTypeValue
} from '@/api/robot-actions'
import swapIcon from '@/assets/rebuild/strategy-swap.svg'
import documentIcon from '@/assets/rebuild/strategy-document.svg'
import chartIcon from '@/assets/rebuild/strategy-chart.svg'
import coinIcon from '@/assets/rebuild/strategy-coin.svg'
import gridIcon from '@/assets/rebuild/strategy-grid.svg'
import martinIcon from '@/assets/rebuild/strategy-martin.svg'
import lightningIcon from '@/assets/rebuild/strategy-lightning.svg'
import emptyRobotIcon from '@/assets/rebuild/robot-empty.svg'

const ICONS = {
  swap: swapIcon,
  document: documentIcon,
  chart: chartIcon,
  coin: coinIcon,
  grid: gridIcon,
  martin: martinIcon
}

export default {
  name: 'RobotsPanel',
  components: { EChartLine, StrategyPositionPreviewModal },
  props: {
    robots: { type: Array, required: true },
    earnings: { type: Object, required: true },
    loading: { type: Boolean, default: false },
    exchange: { type: String, default: '交易所加载中' },
    robotType: { type: Number, default: null },
    errors: { type: Array, default: () => [] },
    robotInfo: { type: Object, default: null },
    allRobotInfos: { type: Array, default: () => [] },
    powerLoading: { type: Boolean, default: false },
    strategyMode: { type: String, default: 'current' },
    strategyListLoading: { type: Boolean, default: false },
    historyPage: { type: Number, default: 1 },
    historyPageSize: { type: Number, default: 10 },
    historyTotal: { type: Number, default: 0 }
  },
  data() {
    return {
      sort: 'default',
      productsExpanded: false,
      mobileOpenGroups: {},
      positionPreviewVisible: false,
      positionPreviewCategory: 'term',
      products: STRATEGY_PRODUCTS,
      emptyRobotIcon
    }
  },
  computed: {
    isHistoryMode() {
      return this.strategyMode === 'history'
    },
    displayStrategyTotal() {
      return this.isHistoryMode ? this.historyTotal : this.visibleRobots.length
    },
    historyPageCount() {
      return Math.max(1, Math.ceil(this.historyTotal / Math.max(1, this.historyPageSize)))
    },
    visibleProducts() {
      if (this.robotType === 2 || this.robotType === 3) {
        return this.products.filter(product => product.key === 'hedge-single')
      }
      if (this.robotType === 0) {
        return this.products.filter(product => product.group !== 'AI 智能产品')
      }
      return this.products
    },
    runningCount() {
      return this.robots.filter(robot => this.effectiveStatus(robot) === 'running').length
    },
    robotPowerState() {
      if (!this.robotInfo) {
        const boundRobots = this.allRobotInfos.filter(item => Number(item && item.bind_api) === 1)
        const hasRunningRobot = boundRobots.some(item => Number(item && item.state) === 1)
        return {
          text: hasRunningRobot ? '机器人运行中' : '启动机器人',
          tone: hasRunningRobot ? 'robot-power--running' : 'robot-power--start',
          mode: hasRunningRobot ? 'running' : 'start',
          icon: hasRunningRobot ? 'el-icon-s-data' : 'el-icon-video-play',
          disabled: false,
          title: hasRunningRobot ? '点击关闭全部机器人' : '点击启动全部机器人'
        }
      }
      if (Number(this.robotInfo.bind_api) !== 1) {
        return {
          text: '未绑定 API',
          tone: 'robot-power--disabled',
          mode: 'disabled',
          icon: 'el-icon-warning-outline',
          disabled: false,
          title: `请先绑定 ${this.exchange} 交易所 API`
        }
      }
      const state = Number(this.robotInfo.state)
      if (state === 1) {
        return {
          text: '机器人运行中',
          tone: 'robot-power--running',
          mode: 'running',
          icon: 'el-icon-s-data',
          disabled: false,
          title: '点击关闭机器人'
        }
      }
      if (state === 2) {
        return {
          text: '启动机器人',
          tone: 'robot-power--start',
          mode: 'start',
          icon: 'el-icon-video-play',
          disabled: false,
          title: '点击启动机器人'
        }
      }
      return {
        text: state === 3 ? '机器人已删除' : '服务切换中',
        tone: 'robot-power--disabled',
        mode: 'disabled',
        icon: 'el-icon-warning-outline',
        disabled: true,
        title: '当前状态不可操作'
      }
    },
    knownHealth() {
      return this.robots
        .map(robot => finiteNumber(robot.health))
        .filter(value => value !== null)
    },
    averageHealthText() {
      if (!this.knownHealth.length) return '--'
      const average = this.knownHealth.reduce((sum, value) => sum + value, 0) / this.knownHealth.length
      return `${formatNumber(average, 0)}%`
    },
    todayExecutionText() {
      const values = this.robots
        .map(robot => firstNumber(robot.raw || {}, [
          'today_order_count',
          'today_order_num',
          'today_trade_count',
          'order_count_today'
        ]))
        .filter(value => value !== null)
      if (!values.length) return '--'
      return formatNumber(values.reduce((sum, value) => sum + value, 0), 0)
    },
    systemStatus() {
      if (this.loading) return { text: '数据同步中', tone: 'syncing' }
      if (this.errors.length) return { text: '数据异常', tone: 'warning' }
      if (this.runningCount) return { text: '系统在线', tone: 'online' }
      return { text: '机器人已关闭', tone: 'offline' }
    },
    featuredProducts() {
      return [
        {
          product: this.visibleProducts.find(item => item.key === 'hedge-speed'),
          icon: lightningIcon,
          iconTone: 'blue',
          label: '主推 · 热门',
          description: '高频出手、快进快出',
          return30dText: '+28.4%'
        },
        {
          product: this.visibleProducts.find(item => item.key === 'hedge-single'),
          icon: chartIcon,
          iconTone: 'teal',
          label: '主推 · 稳健',
          description: '顺势单向持仓、回撤可控',
          return30dText: '+16.9%'
        }
      ].filter(item => item.product)
    },
    productGroups() {
      return this.visibleProducts.reduce((groups, product) => {
        let group = groups.find(item => item.name === product.group)
        if (!group) {
          group = { name: product.group, products: [] }
          groups.push(group)
        }
        group.products.push(product)
        return groups
      }, [])
    },
    mobileProducts() {
      return this.visibleProducts.map(product => {
        const trend = this.productTrend(product)
        const returnValue = finiteNumber(this.mobileProductCopy(product).return30d)
        return {
          ...product,
          mobileBadge: product.key === 'astra'
            ? '中等'
            : (product.badge || (product.hot ? '推荐' : '策略')),
          mobileLabel: this.mobileProductLabel(product),
          trendPoints: trend.points,
          trendLabels: trend.labels,
          returnValue,
          returnText: returnValue === null ? '--' : `${returnValue > 0 ? '+' : ''}${formatNumber(returnValue, 1)}%`
        }
      })
    },
    visibleRobots() {
      const rows = this.robots.slice()
      if (this.sort === 'profit') {
        rows.sort((a, b) => (finiteNumber(b.profitValue) || 0) - (finiteNumber(a.profitValue) || 0))
      }
      if (this.sort === 'health') {
        rows.sort((a, b) => (finiteNumber(b.health) || -1) - (finiteNumber(a.health) || -1))
      }
      return rows
    }
  },
  methods: {
    positionPreviewCategoryForGroup(group) {
      const categories = {
        '合约产品': 'hedge',
        '期现产品': 'term'
      }
      return categories[group && group.name] || ''
    },
    canPreviewGroup(group) {
      return Boolean(this.positionPreviewCategoryForGroup(group))
    },
    openPositionPreview(group) {
      const category = this.positionPreviewCategoryForGroup(group)
      if (!category) return
      this.positionPreviewCategory = category
      this.positionPreviewVisible = true
    },
    mobileProductGroupOpen(group, index) {
      if (Object.prototype.hasOwnProperty.call(this.mobileOpenGroups, group.name)) {
        return this.mobileOpenGroups[group.name]
      }
      return index === 0
    },
    toggleMobileProductGroup(group, index) {
      this.$set(this.mobileOpenGroups, group.name, !this.mobileProductGroupOpen(group, index))
    },
    mobileProductGroupSubtitle(group) {
      const subtitles = {
        'AI 智能产品': 'AI智能化策略',
        '合约产品': '合约策略与风险保护',
        '期现产品': '现货与合约组合策略',
        '现货产品': '现货自动交易策略'
      }
      return subtitles[group.name] || '自动量化策略'
    },
    mobileProductView(product) {
      return this.mobileProducts.find(item => item.key === product.key) || {
        returnValue: null,
        returnText: '--',
        trendPoints: [],
        trendLabels: []
      }
    },
    mobileProductCopy(product) {
      const copies = {
        astra: {
          return30d: 32.6,
          tag: '★ PRIME · 旗舰',
          description: '100 USDT 开启，AI 智能选指标并自动运行',
          risk: '中高',
          tagColor: '#B7791F',
          tagBackground: '#FDF6E3'
        },
        'futures-x-pro': {
          return30d: 24.8,
          tag: '推荐 · 热门',
          description: '适合主流币种',
          risk: '中',
          tagColor: '#FF7A1A',
          tagBackground: '#FFF3E8'
        },
        'futures-x': {
          return30d: 9.6,
          tag: '风险最低',
          description: '风险最低的合约策略',
          risk: '低',
          tagColor: '#0B7A47',
          tagBackground: '#E8F6EE'
        },
        'hedge-standard': {
          return30d: 66.8,
          tag: '年800%+ · V3.0',
          description: '多空对冲震荡套利，风险可控',
          risk: '中高',
          tagColor: '#1F6BFF',
          tagBackground: '#EBF1FE'
        },
        'hedge-speed': {
          return30d: 128.4,
          tag: '年2000%+ · 主推',
          description: '风险不敏感，追求高额收益，可忍受较多本金损失',
          risk: '高',
          tagColor: '#F04D23',
          tagBackground: '#FFF0EA'
        },
        'hedge-single': {
          return30d: 82.6,
          tag: '年1000%+ · 主推',
          description: '建议做多方向',
          risk: '中高',
          tagColor: '#F04D23',
          tagBackground: '#FFF0EA'
        },
        'term-standard': {
          return30d: 8.4,
          tag: '年100%+',
          description: '现货合约无限对冲，无惧风险，不止损、不爆仓',
          risk: '低',
          tagColor: '#1F6BFF',
          tagBackground: '#EBF1FE'
        },
        'term-speed': {
          return30d: 16.8,
          tag: '年200%+',
          description: '比普通版收益高，覆盖范围适中',
          risk: '中',
          tagColor: '#1F6BFF',
          tagBackground: '#EBF1FE'
        },
        'term-btc': {
          return30d: 34.2,
          tag: '年400%+',
          description: '收益最高的版本，下跌覆盖 30% 左右，适合主流币',
          risk: '中',
          tagColor: '#1F6BFF',
          tagBackground: '#EBF1FE'
        },
        bb: {
          return30d: 9.2,
          tag: '年100%+',
          description: '双币种交易套利，适合横盘屯币',
          risk: '低',
          tagColor: '#1F6BFF',
          tagBackground: '#EBF1FE'
        }
      }
      return copies[product.key] || {}
    },
    mobileProductDescription(product) {
      return this.mobileProductCopy(product).description || product.description
    },
    mobileProductTag(product) {
      return this.mobileProductCopy(product).tag || product.badge || (product.hot ? '推荐' : '策略')
    },
    mobileProductTagStyle(product) {
      const copy = this.mobileProductCopy(product)
      if (copy.tagColor || copy.tagBackground) {
        return {
          color: copy.tagColor,
          background: copy.tagBackground
        }
      }
      const tag = product.tags && product.tags[0]
      if (!tag) return {}
      return {
        color: tag.color,
        background: tag.background
      }
    },
    mobileProductRisk(product) {
      return this.mobileProductCopy(product).risk || '中'
    },
    mobileProductRiskStyle(product) {
      const risk = this.mobileProductRisk(product)
      if (risk === '低') return { color: '#0B7A47', background: '#E8F6EE' }
      if (risk === '高') return { color: '#D92D20', background: '#FCEAE8' }
      return { color: '#B45309', background: '#FBF1DF' }
    },
    productTrend(product) {
      const source = [
        product.trend,
        product.trendData,
        product.profitTrend,
        product.profit_chart,
        product.chartData
      ].find(Array.isArray)
      if (!source || source.length < 2) return { points: [], labels: [] }

      const points = []
      const labels = []
      source.forEach((entry, index) => {
        const value = typeof entry === 'number'
          ? entry
          : firstNumber(entry || {}, ['value', 'profit', 'profit_rate', 'rate', 'yield'])
        if (value === null) return
        points.push(value)
        labels.push(String(
          entry && typeof entry === 'object'
            ? (entry.date || entry.time || entry.label || index + 1)
            : index + 1
        ))
      })
      return points.length > 1 ? { points, labels } : { points: [], labels: [] }
    },
    mobileProductLabel(product) {
      if (product.key === 'astra') return '★ PRIME · 旗舰'
      if (product.tags && product.tags.length) return product.tags[0].text
      if (product.hot) return '推荐 · 热门'
      return product.badge || ''
    },
    mobileStrategyTitle(robot) {
      return robot.name || this.robotPair(robot)
    },
    mobileStrategyMeta(robot) {
      const pair = this.robotPairParts(robot).join('/') || this.robotPair(robot)
      const parts = [pair, robot.exchange]
      if (finiteNumber(robot.investmentValue) !== null) {
        parts.push(`${this.$t('投入')} ${robot.investment} ${robot.investmentUnit || 'USDT'}`)
      }
      return parts.filter(value => value && value !== '--').join(' · ') || '--'
    },
    mobileTodayProfitValue(robot) {
      return firstNumber(robot.raw || {}, [
        'today_profit',
        'profit_today',
        'today_profit_total',
        'profit_total_today',
        'day_profit',
        'profit_day'
      ])
    },
    mobileTodayProfitText(robot) {
      return formatSigned(this.mobileTodayProfitValue(robot))
    },
    actionCapabilities(robot) {
      return robotActionCapabilities(robot)
    },
    canModify(robot) {
      const capabilities = this.actionCapabilities(robot)
      if (!capabilities.modify) return false
      if (['hedge', 'term', 'astra', 'futures_x', 'bb'].includes(robot.productType)) {
        return robot.status === 'running'
      }
      return true
    },
    effectiveStatus(robot) {
      const robotType = robotTypeValue(robot)
      const robotInfo = this.allRobotInfos.find(item =>
        Number(item && (
          item.robot_type !== undefined
            ? item.robot_type
            : item.robotType
        )) === robotType
      )
      const robotState = robotInfo && robotInfo.state
      if (
        robotState !== null &&
        robotState !== undefined &&
        robotState !== '' &&
        Number(robotState) !== 1
      ) {
        return 'paused'
      }
      return robot.status
    },
    maxPositionException(robot) {
      return (
        robot.status === 'paused' &&
        robotNeedsMaximumRestart(robot)
      )
    },
    signed(value) {
      return formatSigned(value)
    },
    valueTone(value) {
      const number = finiteNumber(value)
      if (number === null) return ''
      return number < 0 ? 'negative' : 'positive'
    },
    iconFor(kind) {
      return ICONS[kind] || swapIcon
    },
    productForRobot(robot) {
      const raw = robot.raw || {}
      const type = robot.productType
      if (type === 'astra') return this.products.find(item => item.key === 'astra')
      if (type === 'futures_x') {
        const version = Number(raw.version_type || raw.versionType)
        return this.products.find(item => item.key === (version === 2 ? 'futures-x-pro' : 'futures-x'))
      }
      if (type === 'hedge') {
        const speed = Number(raw.speed)
        return this.products.find(item => item.key === (
          speed === 2 ? 'hedge-single' : speed === 1 ? 'hedge-speed' : 'hedge-standard'
        ))
      }
      if (String(type).toLowerCase() === 'term') {
        const version = Number(raw.version)
        return this.products.find(item => item.key === (
          version === 5 ? 'term-btc' : version === 3 ? 'term-speed' : 'term-standard'
        ))
      }
      if (type === 'bb') return this.products.find(item => item.key === 'bb')
      if (type === 'grid') return this.products.find(item => item.key === 'grid')
      if (type === 'martin') return this.products.find(item => item.key === 'martin')
      return null
    },
    productIcon(robot) {
      const product = this.productForRobot(robot)
      return this.iconFor(product && product.icon)
    },
    productGradient(robot) {
      const product = this.productForRobot(robot)
      return (product && product.gradient) || 'linear-gradient(135deg,#5FA5F9,#1F6BFF)'
    },
    productRobotCount(product) {
      const count = this.robots.filter(robot => {
        const current = this.productForRobot(robot)
        return current && current.key === product.key
      }).length
      return count || '--'
    },
    robotBase(robot) {
      const symbol = String(robot.symbol || '').replace(/[_-]/g, '/')
      if (!symbol || symbol === '--') return '--'
      const parts = symbol.split('/')
      return parts[1] || parts[0]
    },
    isMultiCoinComprehensive(robot) {
      const raw = robot.raw || {}
      return (
        String(robot.productType || '').toLowerCase() === 'futures_x' &&
        String(raw.futures_version || '').toLowerCase() === 'x_vip_astra'
      )
    },
    robotPair(robot) {
      if (String(robot.productType).toLowerCase() === 'astra') return 'UPlus-Astra AI'
      if (this.isMultiCoinComprehensive(robot)) {
        return `UPlus-${this.$tk('robot.multi_coin_comprehensive')}`
      }
      const symbol = String(robot.symbol || '').replace(/[_-]/g, '/')
      if (!symbol || symbol === '--') return '--'
      return symbol.split('/').filter(Boolean).join(' → ')
    },
    robotPairParts(robot) {
      if (this.isMultiCoinComprehensive(robot)) return []
      const symbol = String(robot.symbol || '').replace(/[_-]/g, '/')
      if (!symbol || symbol === '--') return []
      return symbol.split('/').filter(Boolean)
    },
    positionArrow(robot) {
      if (robot.positionDirection === 'both') return '⇄'
      if (robot.positionDirection === 'left') return '←'
      return '→'
    },
    robotKind(robot) {
      const type = String(robot.productType || '').toLowerCase()
      if (type === 'hedge' || type === 'futures_x' || type === 'astra') return '合约'
      if (type === 'term') return '期现'
      return '现货'
    },
    stopLossText(robot) {
      const value = firstNumber(robot.raw || {}, ['stop_loss', 'stopLoss'])
      if (value === null) return '--'
      return value === 0 ? '关闭' : '已开启'
    },
    riskStatus(robot) {
      const health = finiteNumber(robot.health)
      if (health === null) return { text: '未知', tone: 'unknown' }
      if (health >= 80) return { text: '低', tone: 'low' }
      if (health >= 60) return { text: '中', tone: 'medium' }
      return { text: '高', tone: 'high' }
    },
    statusText(status) {
      if (status === 'running') return '运行中'
      if (status === 'paused') return '已暂停'
      return '状态未知'
    },
    profitIcon(value) {
      const number = finiteNumber(value)
      if (number === null) return ''
      return number < 0 ? 'el-icon-caret-bottom' : 'el-icon-caret-top'
    },
    healthWidth(robot) {
      const health = finiteNumber(robot.health)
      if (health === null) return '0%'
      return `${Math.max(0, Math.min(100, health))}%`
    },
    healthColor(robot) {
      const health = finiteNumber(robot.health)
      if (health === null) return '#C7CCD6'
      if (health >= 80) return '#0E9F5C'
      if (health >= 60) return '#E3A92E'
      return '#E5484D'
    },
    healthText(robot) {
      const health = finiteNumber(robot.health)
      return health === null ? '--' : `${formatNumber(health, 0)}%`
    },
    strategyTimeText(robot) {
      if (this.isHistoryMode) {
        const raw = robot.raw || {}
        const deletedAt = raw.delete_time || raw.deleted_at || raw.end_time
        if (deletedAt) return String(deletedAt).replace('T', ' ').slice(0, 16)
      }
      return this.createdAtText(robot)
    },
    createdAtText(robot) {
      const raw = robot.raw || {}
      const text = raw.add_time || raw.create_time || raw.created_at
      return text ? String(text).replace('T', ' ').slice(0, 16) : '--'
    },
    shortCreatedAtText(robot) {
      const text = this.createdAtText(robot)
      return text === '--' ? text : text.slice(5)
    },
    exportHistory() {
      if (!this.robots.length) {
        this.$message.info('暂无策略记录可导出')
        return
      }
      const rows = [
        ['策略', '交易所', '交易对', '投入金额', '累计收益', '收益率', '健康度', '状态'].map(label => this.$t(label)),
        ...this.robots.map(robot => [
          this.$t(robot.name),
          robot.exchange,
          robot.symbol,
          robot.investment,
          robot.profit,
          robot.rate,
          this.$t(this.healthText(robot)),
          this.$t(this.statusText(this.effectiveStatus(robot)))
        ])
      ]
      const csv = rows
        .map(row => row.map(value => `"${String(value).replace(/"/g, '""')}"`).join(','))
        .join('\r\n')
      const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `UPlus-${this.$t('策略记录')}-${new Date().toISOString().slice(0, 10)}.csv`
      link.click()
      URL.revokeObjectURL(url)
    }
  }
}
</script>

<style scoped>
.robots-panel {
  min-width: 1120px;
  color: #1b1f2a;
  animation: up-page-in .25s ease;
}

.robots-page-head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 20px;
}

.robots-page-head h1,
.robots-page-head p,
.robot-section-title h2 {
  margin: 0;
}

.robots-page-head h1 {
  font-size: 24px;
  font-weight: 800;
  letter-spacing: .2px;
}

.robots-page-head p {
  margin-top: 5px;
  color: #8a90a0;
  font-size: 13px;
}

.robots-page-head__actions {
  display: flex;
  gap: 10px;
}

.robot-power,
.robot-export {
  height: 38px;
  border-radius: 8px;
  padding: 0 17px;
  font-size: 14px;
  font-weight: 700;
  white-space: nowrap;
}

.robot-power {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  cursor: pointer;
  transition:
    color .16s ease,
    background-color .16s ease,
    border-color .16s ease,
    box-shadow .16s ease,
    transform .1s ease;
}

.robot-power--running {
  color: #0b7a47;
  border: 1px solid #bfe8d2;
  background: #f0faf4;
}

.robot-power--running:not(:disabled):hover {
  color: #d92d20;
  border-color: #f3d2cf;
  background: #fceae8;
}

.robot-power--start {
  color: #fff;
  border: 1px solid #0e9f5c;
  background: #0e9f5c;
}

.robot-power--start:not(:disabled):hover {
  border-color: #0b7a47;
  background: #0b7a47;
}

.robot-power:not(:disabled):active {
  transform: translateY(1px);
}

.robot-power:focus-visible {
  outline: 3px solid rgba(31, 107, 255, .18);
  outline-offset: 2px;
}

.robot-power__bars,
.robot-power__play {
  flex: 0 0 auto;
  display: block;
}

.robot-power--disabled {
  color: #8a90a0;
  border: 1px solid #dcdfe8;
  background: #f7f8fa;
}

.robot-power:disabled {
  cursor: not-allowed;
  opacity: .72;
}

.robot-export {
  color: #3a3f4e;
  border: 1px solid #dcdfe8;
  background: #fff;
}

.robot-export:hover {
  color: #1f6bff;
  border-color: #a9c4f7;
}

.robot-status-card {
  display: flex;
  align-items: center;
  gap: 32px;
  padding: 22px 26px;
  overflow-x: auto;
  white-space: nowrap;
  border: 1px solid #e7eaf1;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(16,24,40,.04), 0 6px 20px rgba(16,24,40,.04);
  transition: .18s ease;
}

.robot-status-card:hover,
.product-group:hover,
.robot-list-card:hover {
  border-color: #a9c4f7;
  box-shadow: 0 14px 34px rgba(16,24,40,.10);
  transform: translateY(-2px);
}

.robot-stat > span {
  display: block;
  color: #79808f;
  font-size: 13px;
}

.robot-stat > strong {
  display: block;
  margin-top: 5px;
  font-size: 24px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
}

.robot-stat small {
  color: #8a90a0;
  font-size: 13px;
  font-weight: 600;
}

.positive {
  color: #0b9b57 !important;
}

.negative {
  color: #e5484d !important;
}

.robot-stat-divider {
  width: 1px;
  height: 40px;
  flex: 0 0 auto;
  background: #f0f2f6;
}

.robot-system-pill {
  display: flex;
  margin-left: auto;
  align-items: center;
  gap: 9px;
  flex-shrink: 0;
  border-radius: 999px;
  padding: 9px 16px;
  font-size: 13px;
  font-weight: 700;
}

.robot-system-pill i {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  animation: up-pulse 2s ease infinite;
}

.robot-system-pill.online {
  color: #0b7a47;
  background: #f0faf4;
}

.robot-system-pill.online i {
  background: #12b76a;
}

.robot-system-pill.syncing,
.robot-system-pill.offline {
  color: #5a6072;
  background: #f1f3f8;
}

.robot-system-pill.syncing i,
.robot-system-pill.offline i {
  background: #98a0b0;
}

.robot-system-pill.warning {
  color: #91600a;
  background: #fbf1df;
}

.robot-system-pill.warning i {
  background: #e3a92e;
}

.robot-section-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
  margin: 36px 0 14px;
}

.robot-section-title > div,
.product-title__meta {
  display: flex;
  align-items: center;
  gap: 12px;
}

.robot-section-title > div > i {
  width: 4px;
  height: 20px;
  border-radius: 99px;
}

.robot-section-title > div > i.blue {
  background: #1f6bff;
}

.robot-section-title > div > i.green {
  background: #0e9f5c;
}

.robot-section-title h2 {
  font-size: 18px;
  font-weight: 800;
  letter-spacing: .2px;
}

.section-tag {
  border-radius: 999px;
  padding: 3px 11px;
  font-size: 11.5px;
  font-weight: 800;
}

.section-tag.blue {
  color: #1f6bff;
  background: #ebf1fe;
}

.section-tag.green {
  color: #0b7a47;
  background: #e8f6ee;
}

.product-title__meta > span {
  color: #8a90a0;
  font-size: 12.5px;
}

.product-title__meta button {
  color: #1f6bff;
  border: 1px solid #dce8fd;
  border-radius: 8px;
  padding: 7px 14px;
  background: #fff;
  font-size: 12.5px;
  font-weight: 700;
}

.product-title__meta button:hover {
  background: #f3f7fe;
}

.featured-products {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;
}

.featured-product {
  position: relative;
  display: flex;
  min-width: 0;
  overflow: hidden;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  text-align: left;
  color: #fff;
  border: 0;
  border-radius: 14px;
  background:
    radial-gradient(560px 280px at 88% -30%, rgba(255,170,80,.22), transparent 60%),
    linear-gradient(130deg,#081737,#123289 58%,#1d5bd8);
  box-shadow: 0 16px 40px rgba(8,23,55,.18);
  transition: .18s ease;
}

.featured-product:nth-child(2) {
  background:
    radial-gradient(560px 280px at 88% -30%, rgba(52,217,184,.25), transparent 60%),
    linear-gradient(130deg,#081737,#123289 58%,#1d5bd8);
}

.featured-product:hover {
  box-shadow: 0 22px 52px rgba(8,23,55,.32);
  transform: translateY(-2px);
}

@keyframes product-sweep {
  0% { transform: translateX(-300px) skewX(-16deg); }
  60%, 100% { transform: translateX(2100px) skewX(-16deg); }
}

@keyframes product-glow {
  0%, 100% { box-shadow: 0 10px 26px rgba(237,190,75,.35); }
  50% { box-shadow: 0 14px 38px rgba(237,190,75,.65); }
}

.featured-product__sweep {
  position: absolute;
  top: -40%;
  bottom: -40%;
  left: 0;
  width: 110px;
  pointer-events: none;
  background: linear-gradient(105deg, transparent, rgba(255,255,255,.13), transparent);
  animation: product-sweep 3.6s ease-in-out infinite;
}

.featured-product__icon {
  display: grid;
  width: 42px;
  height: 42px;
  flex: 0 0 auto;
  place-items: center;
  border-radius: 11px;
}

.featured-product__icon.blue {
  background: #1f6bff;
}

.featured-product__icon.teal {
  background: #0b9ba8;
}

.featured-product__icon img {
  width: 22px;
  height: 22px;
}

.featured-product__body {
  min-width: 0;
  flex: 1;
}

.featured-product__name {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.featured-product__name strong {
  font-size: 16px;
  font-weight: 800;
  letter-spacing: .3px;
  white-space: nowrap;
}

.featured-product__name em {
  color: #fff;
  border-radius: 6px;
  padding: 2px 8px;
  background: linear-gradient(135deg,#ff8a3c,#f04d23);
  font-size: 11px;
  font-style: normal;
  font-weight: 800;
  white-space: nowrap;
}

.featured-product__body small {
  display: block;
  margin-top: 4px;
  overflow: hidden;
  color: rgba(255,255,255,.62);
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.featured-product__body small b {
  color: #9fe8c3;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.featured-product__performance {
  flex: 0 0 auto;
  text-align: right;
}

.featured-product__performance small {
  display: block;
  color: rgba(255,255,255,.6);
  font-size: 11px;
}

.featured-product__performance strong {
  display: block;
  color: #6ef0d2;
  font-size: 23px;
  font-weight: 800;
  line-height: 1.25;
}

.featured-product:first-child .featured-product__performance strong {
  color: #edbe4b;
}

.featured-product__create {
  flex: 0 0 auto;
  color: #4a3403;
  border-radius: 9px;
  padding: 10px 18px;
  background: linear-gradient(135deg,#f9e3a0,#edbe4b);
  font-size: 13.5px;
  font-weight: 800;
  white-space: nowrap;
  animation: product-glow 2.2s ease infinite;
  transition: transform .15s ease;
}

.featured-product:hover .featured-product__create {
  transform: scale(1.06);
}

.product-groups {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-bottom: 26px;
}

.product-group {
  padding: 4px 22px 8px;
  border: 1px solid #e7eaf1;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(16,24,40,.04), 0 6px 20px rgba(16,24,40,.04);
  transition: .18s ease;
}

.product-group header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 0 10px;
}

.product-group header > div {
  display: flex;
  align-items: center;
  gap: 8px;
}

.product-group header > div > i {
  width: 10px;
  height: 10px;
  border-radius: 3px;
  box-shadow: 0 2px 6px rgba(16,24,40,.2);
}

.product-group header strong {
  font-size: 15px;
  font-weight: 800;
}

.product-group__info {
  display: grid;
  width: 14px;
  height: 14px;
  place-items: center;
  color: #b7bdcb;
  border: 1.3px solid #b7bdcb;
  border-radius: 50%;
  font: 800 9px/1 Arial, sans-serif;
}

.product-group header em {
  color: #79808f;
  border-radius: 999px;
  padding: 2px 9px;
  background: #f1f3f8;
  font-size: 11px;
  font-style: normal;
  font-weight: 800;
}

.product-group__preview {
  display: inline-flex;
  padding: 5px 8px;
  align-items: center;
  gap: 5px;
  color: #1f6bff;
  border: 0;
  border-radius: 6px;
  background: transparent;
  font-family: inherit;
  font-size: 12.5px;
  font-weight: 700;
  cursor: pointer;
}
.product-group__preview:hover {
  background: #eef4ff;
}

.product-row {
  display: flex;
  width: 100%;
  align-items: center;
  gap: 14px;
  margin: 0 -8px;
  padding: 14px 0 14px 8px;
  text-align: left;
  border: 0;
  border-top: 1px solid #f3f4f8;
  border-radius: 10px;
  background: transparent;
  transition: background .15s ease;
}

.product-row:hover {
  background: #f7faff;
}

.product-row__icon {
  display: grid;
  width: 44px;
  height: 44px;
  flex: 0 0 auto;
  place-items: center;
  border-radius: 12px;
}

.product-row__icon img {
  width: 22px;
  height: 22px;
}

.product-row__copy {
  min-width: 0;
  flex: 1;
}

.product-row__name {
  display: flex;
  align-items: center;
  gap: 7px;
  flex-wrap: wrap;
}

.product-row__name strong {
  font-size: 14.5px;
  font-weight: 800;
  white-space: nowrap;
}

.product-row__name em {
  border-radius: 6px;
  padding: 2.5px 9px;
  font-size: 11.5px;
  font-style: normal;
  font-weight: 800;
  white-space: nowrap;
}

.product-row__name em.hot {
  color: #fff;
  background: linear-gradient(135deg,#ff8a3c,#f04d23);
}

.product-row__copy > small {
  display: block;
  margin-top: 4px;
  color: #79808f;
  font-size: 12.5px;
  line-height: 1.6;
}

.product-row__add {
  display: grid;
  width: 32px;
  height: 32px;
  flex: 0 0 auto;
  place-items: center;
  color: #fff;
  border-radius: 50%;
  background: linear-gradient(135deg,#4a8cff,#1252e0);
  box-shadow: 0 6px 14px rgba(31,107,255,.32);
  font-size: 16px;
  font-weight: 700;
  transition: .15s ease;
}

.product-row:hover .product-row__add {
  box-shadow: 0 8px 18px rgba(31,107,255,.45);
  transform: scale(1.12);
}

.product-chips {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 26px;
  padding: 14px 20px;
  border: 1px solid #e7eaf1;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(16,24,40,.04), 0 6px 20px rgba(16,24,40,.04);
}

.product-chips button {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #3a3f4e;
  border: 1px solid #e7eaf1;
  border-radius: 999px;
  padding: 7px 14px;
  background: #fff;
  font-size: 12.5px;
  font-weight: 700;
  transition: .15s ease;
}

.product-chips button:hover {
  color: #1f6bff;
  border-color: #a9c4f7;
  background: #f7faff;
}

.product-chips button > i {
  width: 8px;
  height: 8px;
  border-radius: 3px;
}

.product-chips button > strong {
  color: #1f6bff;
}

.product-chips .product-chips__more {
  margin-left: auto;
  color: #1f6bff;
  border-color: transparent;
  white-space: nowrap;
}

.robot-section-title.current-title {
  align-items: center;
  flex-direction: row;
  flex-wrap: nowrap;
}

.current-title__main {
  min-width: 0;
  flex: 1 1 auto;
  flex-wrap: wrap;
}

.strategy-list-tab {
  position: relative;
  flex: 0 0 auto;
  padding: 2px 0 4px;
  border: 0;
  background: transparent;
  color: #79808f;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.4;
  cursor: pointer;
}

.strategy-list-tab::after {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  height: 2px;
  border-radius: 2px;
  background: transparent;
  content: '';
}

.strategy-list-tab.active {
  color: #101828;
  font-weight: 800;
}

.strategy-list-tab.active::after {
  background: #0e9f5c;
}

.strategy-list-tab--history {
  margin-left: auto;
}

.history-strategy-pagination {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid #eef0f4;
}

.history-strategy-pagination button {
  min-width: 84px;
  height: 34px;
  padding: 0 14px;
  border: 1px solid #d8deea;
  border-radius: 8px;
  background: #fff;
  color: #344054;
  font-size: 13px;
  cursor: pointer;
}

.history-strategy-pagination button:hover:not(:disabled) {
  border-color: #1f6bff;
  color: #1f6bff;
}

.history-strategy-pagination button:disabled {
  color: #a7adba;
  cursor: not-allowed;
}

.history-strategy-pagination span {
  min-width: 54px;
  color: #5a6072;
  font-size: 13px;
  text-align: center;
}

.current-title__summary {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  color: #5a6072;
  font-size: 12.5px;
  font-weight: 600;
}

.current-title__summary > i {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #12b76a;
  animation: up-pulse 2s ease infinite;
}

.robot-list-card {
  overflow: hidden;
  border: 1px solid #e7eaf1;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(16,24,40,.04), 0 6px 20px rgba(16,24,40,.04);
  transition: .18s ease;
}

.robot-list-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px 12px;
}

.robot-list-toolbar > span {
  color: #8a90a0;
  font-size: 12.5px;
  font-weight: 600;
}

.robot-list-toolbar > div {
  display: flex;
  align-items: center;
  gap: 10px;
}

.robot-sort {
  display: flex;
  gap: 4px;
  padding: 3px;
  border-radius: 9px;
  background: #f5f6f9;
}

.robot-sort button {
  color: #79808f;
  border: 0;
  border-radius: 7px;
  padding: 6px 13px;
  background: transparent;
  font-size: 12.5px;
  font-weight: 700;
}

.robot-sort button.active {
  color: #1f6bff;
  background: #fff;
}

.robot-list-current-exchange {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  color: #3a3f4e;
  border: 1px solid #dfe3ec;
  border-radius: 8px;
  padding: 8px 10px;
  background: #fff;
  font-size: 13px;
  font-weight: 600;
}

.robot-list-current-exchange i {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #0e9f5c;
}

.robot-empty {
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 14px;
  padding: 56px 24px 60px;
  border-top: 1px solid #f3f4f8;
}

.robot-empty > span {
  display: grid;
  width: 76px;
  height: 76px;
  place-items: center;
  border-radius: 50%;
  background: #f0f5fe;
}

.robot-empty > span img {
  width: 36px;
  height: 36px;
}

.robot-empty > strong {
  font-size: 16px;
  font-weight: 800;
}

.robot-empty p {
  margin: 0;
  color: #8a90a0;
  font-size: 13px;
  line-height: 1.7;
  text-align: center;
}

.robot-empty button {
  margin-top: 4px;
  color: #fff;
  border: 0;
  border-radius: 8px;
  padding: 11px 26px;
  background: #1f6bff;
  font-size: 14px;
  font-weight: 700;
}

.robot-strategy {
  padding: 15px 24px 13px;
  border-top: 1px solid #f3f4f8;
}

.robot-strategy:hover {
  background: #fafbfe;
}

.robot-strategy__head {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.robot-strategy__icon {
  display: grid;
  width: 34px;
  height: 34px;
  flex: 0 0 auto;
  place-items: center;
  border-radius: 10px;
}

.robot-strategy__icon img {
  width: 17px;
  height: 17px;
}

.robot-strategy__head > button {
  padding: 0;
  color: #1b1f2a;
  border: 0;
  background: transparent;
  font-size: 15.5px;
  font-weight: 800;
  letter-spacing: .2px;
}

.robot-strategy__head > button:hover {
  color: #1f6bff;
}

.robot-strategy__identity {
  display: flex;
  min-width: 150px;
  align-items: flex-start;
  flex-direction: column;
  gap: 3px;
  text-align: left;
}

.robot-strategy__identity strong {
  font-size: 15.5px;
  line-height: 19px;
}

.robot-pair-arrow {
  display: inline-block;
  min-width: 20px;
  margin: 0 2px;
  text-align: center;
  color: #12b76a;
  font-size: 17px;
  font-weight: 800;
  line-height: 16px;
  vertical-align: -1px;
}

.robot-pair-arrow--left {
  color: #e5484d;
}

.robot-pair-arrow--both {
  color: #12a594;
}

.robot-strategy__identity small {
  color: #8a90a0;
  font-size: 11px;
  font-weight: 500;
  line-height: 14px;
}

.robot-strategy__identity:hover small {
  color: #6f7d96;
}

.robot-strategy__tag {
  color: #1f6bff;
  border-radius: 6px;
  padding: 3px 10px;
  background: #ebf1fe;
  font-size: 11.5px;
  font-weight: 800;
}

.robot-strategy__tag.muted {
  color: #5a6072;
  background: #f1f3f8;
}

.robot-strategy__trend {
  color: #98a0af;
  font-size: 11px;
}

.robot-strategy__states {
  display: flex;
  margin-left: auto;
  align-items: center;
  gap: 10px;
}

.robot-strategy__states em {
  border-radius: 999px;
  padding: 4px 11px;
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
}

.robot-strategy__states em.low,
.robot-strategy__states em.running {
  color: #0b7a47;
  background: #e8f6ee;
}

.robot-strategy__states em.medium {
  color: #91600a;
  background: #fbf1df;
}

.robot-strategy__states em.high {
  color: #b42318;
  background: #fceae8;
}

.robot-strategy__states em.unknown,
.robot-strategy__states em.paused {
  color: #5a6072;
  background: #f1f3f8;
}

.robot-strategy__states em i {
  display: inline-block;
  width: 6px;
  height: 6px;
  margin-right: 6px;
  border-radius: 50%;
  background: #98a0af;
}

.robot-strategy__states em.running i {
  background: #12b76a;
}

.robot-strategy__exception-state {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 11px;
  color: #8a5a12;
  border: 1px solid #f0d49b;
  border-radius: 999px;
  background: #fff7e8;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition:
    color .15s ease,
    border-color .15s ease,
    background-color .15s ease;
}

.robot-strategy__exception-state:hover {
  color: #b42318;
  border-color: #efc1bd;
  background: #fceae8;
}

.robot-strategy__content {
  display: flex;
  align-items: stretch;
  gap: 14px;
  margin-top: 13px;
}

.robot-metrics {
  display: grid;
  min-width: 560px;
  flex: 1;
  grid-template-columns: 1fr 1.05fr .85fr 1.25fr 1.35fr;
  padding: 12px 0;
  border: 1px solid #eef1f7;
  border-radius: 12px;
  background: #f8fafd;
}

.robot-metrics > div {
  padding: 2px 18px;
}

.robot-metrics > div + div {
  border-left: 1px solid #ebeef4;
}

.robot-metrics > div > span {
  display: block;
  color: #8a90a0;
  font-size: 11.5px;
}

.robot-metrics > div > strong {
  display: block;
  margin-top: 3px;
  font-size: 16px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
}

.robot-metrics strong.created-at {
  margin-top: 6px;
  color: #5a6072;
  font-size: 13.5px;
  font-weight: 600;
}

.robot-health {
  display: flex !important;
  align-items: center;
  gap: 8px;
  margin-top: 9px;
}

.robot-health > i {
  width: 64px;
  height: 6px;
  overflow: hidden;
  border-radius: 99px;
  background: #e8ebf2;
}

.robot-health > i b {
  display: block;
  height: 100%;
  border-radius: 99px;
}

.robot-health > strong {
  color: #5a6072;
  font-size: 13px;
  font-weight: 700;
}

.robot-strategy__actions {
  display: flex;
  justify-content: center;
  flex-direction: column;
  gap: 8px;
}

.robot-strategy__actions button {
  width: 80px;
  height: 34px;
  color: #3a3f4e;
  border: 1px solid #dcdfe8;
  border-radius: 8px;
  padding: 0;
  background: #fff;
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  transition: border-color .16s ease, background .16s ease, color .16s ease, transform .16s ease;
}

.robot-strategy__actions button:hover {
  border-color: #b8c0d0;
  background: #f7f8fb;
}

.robot-strategy__actions button:active {
  transform: translateY(1px);
}

.robot-strategy__actions button.primary {
  color: #1f6bff;
  border: 0;
  background: #eef4ff;
  font-weight: 700;
}

.robot-strategy__actions button.primary:hover {
  background: #e4edff;
}

.robot-strategy__actions button.resume {
  color: #0b7a47;
  border: 0;
  background: #e8f6ee;
  font-weight: 700;
}

.robot-strategy__actions button.resume:hover {
  background: #def2e7;
}

.robot-strategy__actions button.danger {
  color: #e5484d;
  border-color: #f3c0bd;
  background: #fff;
}

.robot-strategy__actions button.danger:hover {
  border-color: #eca7a3;
  background: #fff7f7;
}

@media (max-width: 1320px) {
  .featured-product__performance {
    display: none;
  }

  .featured-product__create {
    padding: 10px 12px;
  }
}

@media (max-width: 1024px) {
  .robots-panel {
    width: 100%;
    min-width: 0;
    max-width: 100%;
  }

  .robot-status-card {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 16px;
    overflow: visible;
    white-space: normal;
  }

  .robot-stat-divider {
    display: none;
  }

  .robot-system-pill {
    grid-column: 1 / -1;
    margin-left: 0;
    justify-self: start;
  }

  .featured-products,
  .product-groups {
    grid-template-columns: 1fr;
  }

  .robot-metrics {
    min-width: 0;
  }
}

@media (max-width: 760px) {
  .robots-page-head {
    align-items: stretch;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 15px;
  }

  .robots-page-head__actions {
    display: grid;
    width: 100%;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }

  .robot-power,
  .robot-export {
    width: 100%;
    min-width: 0;
    min-height: 42px;
    justify-content: center;
    padding: 0 10px;
  }

  .robot-status-card {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 18px 12px;
    padding: 18px;
    border-radius: 12px;
  }

  .robot-status-card:hover,
  .product-group:hover,
  .robot-list-card:hover {
    box-shadow: 0 1px 2px rgba(16,24,40,.04), 0 6px 20px rgba(16,24,40,.04);
    transform: none;
  }

  .robot-stat > strong {
    font-size: 21px;
  }

  .robot-system-pill {
    width: 100%;
    box-sizing: border-box;
    justify-content: center;
  }

  .robot-section-title {
    align-items: flex-start;
    flex-direction: column;
    gap: 10px;
  }

  .current-title__main {
    gap: 8px;
  }

  .strategy-list-tab {
    font-size: 17px;
  }

  .history-strategy-pagination {
    justify-content: center;
    padding: 14px 12px 18px;
  }

  .product-title__meta {
    width: 100%;
    justify-content: space-between;
  }

  .featured-product {
    min-height: 0;
    padding: 18px 16px;
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .featured-product__body {
    min-width: 0;
    flex: 1;
  }

  .featured-product__name,
  .product-row__name {
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .featured-product__create {
    min-height: 40px;
    margin-left: 50px;
  }

  .product-group {
    padding-right: 14px;
    padding-left: 14px;
  }

  .product-group header {
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 8px;
  }

  .product-row {
    min-height: 0;
    gap: 10px;
    margin-right: 0;
    margin-left: 0;
    padding: 14px 0;
  }

  .product-row__copy {
    min-width: 0;
  }

  .product-row__copy > small {
    overflow-wrap: anywhere;
  }

  .product-chips {
    padding: 12px;
    overflow-x: auto;
    flex-wrap: nowrap;
    scrollbar-width: none;
    -webkit-overflow-scrolling: touch;
  }

  .product-chips::-webkit-scrollbar {
    display: none;
  }

  .product-chips button {
    min-height: 38px;
    flex: 0 0 auto;
  }

  .robot-list-toolbar {
    align-items: stretch;
    flex-direction: column;
    gap: 10px;
    padding: 14px;
  }

  .robot-list-toolbar > div {
    width: 100%;
    align-items: stretch;
    flex-direction: column;
  }

  .robot-sort {
    width: 100%;
    overflow-x: auto;
    box-sizing: border-box;
  }

  .robot-sort button {
    min-width: max-content;
    min-height: 36px;
    flex: 1;
  }

  .robot-list-current-exchange {
    min-height: 40px;
    justify-content: center;
  }

  .robot-strategy {
    padding: 15px 14px;
  }

  .robot-strategy__head {
    align-items: flex-start;
  }

  .robot-strategy__icon {
    width: 38px;
    height: 38px;
  }

  .robot-strategy__identity {
    min-width: 0;
    max-width: calc(100% - 52px);
    flex: 1;
  }

  .robot-strategy__identity strong {
    overflow-wrap: anywhere;
  }

  .robot-strategy__states {
    width: 100%;
    margin-left: 50px;
    flex-wrap: wrap;
  }

  .robot-strategy__content {
    flex-direction: column;
    gap: 10px;
  }

  .robot-metrics {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    padding: 4px 0;
  }

  .robot-metrics > div {
    min-width: 0;
    padding: 12px 14px;
  }

  .robot-metrics > div + div {
    border-left: 0;
  }

  .robot-metrics > div:nth-child(even) {
    border-left: 1px solid #ebeef4;
  }

  .robot-metrics > div:nth-child(n + 3) {
    border-top: 1px solid #ebeef4;
  }

  .robot-strategy__actions {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;
  }

  .robot-strategy__actions button {
    width: 100%;
    min-width: 0;
    height: 40px;
  }

  .robot-empty {
    padding: 42px 18px 46px;
  }
}

@media (max-width: 420px) {
  .robots-page-head__actions,
  .robot-status-card,
  .robot-metrics {
    grid-template-columns: 1fr;
  }

  .robot-metrics > div:nth-child(even) {
    border-left: 0;
  }

  .robot-metrics > div:nth-child(n + 2) {
    border-top: 1px solid #ebeef4;
  }

  .robot-strategy__actions {
    grid-template-columns: 1fr 1fr;
  }
}
</style>
